import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { groupsApi, devicesApi, usersApi } from '../services/api';
import { useTenantContextStore } from '../store/tenant-context.store';
import {
  FolderOpen,
  Plus,
  Pencil,
  Trash2,
  X,
  Loader2,
  Car,
  Users,
  ChevronRight,
  Check,
  Layers,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';

interface Group {
  id: string;
  name: string;
  description: string | null;
  color: string;
  visibleToUserIds?: string[];
  deviceCount: number;
  onlineCount: number;
  createdAt: string;
}

interface Device {
  id: string;
  name: string;
  plate: string | null;
  status: string;
}

interface GroupStats {
  totalGroups: number;
  totalDevices: number;
  unassignedDevices: number;
  groupsWithDevices: number;
}

const COLORS = [
  '#6B7280', '#EF4444', '#F97316', '#EAB308', '#22C55E',
  '#14B8A6', '#0EA5E9', '#6366F1', '#A855F7', '#EC4899',
];

export default function GroupsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [selectedGroup, setSelectedGroup] = useState<Group | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Fetch groups
  const { data: groupsData, isLoading } = useQuery({
    queryKey: ['groups', tenantKey],
    queryFn: () => groupsApi.list(),
  });

  // Fetch stats
  const { data: statsData } = useQuery({
    queryKey: ['groups-stats', tenantKey],
    queryFn: () => groupsApi.getStats(),
  });

  // Fetch group details when selected
  const { data: groupDetails } = useQuery({
    queryKey: ['group', selectedGroup?.id, tenantKey],
    queryFn: () => groupsApi.get(selectedGroup!.id),
    enabled: !!selectedGroup,
  });

  const groups: Group[] = groupsData?.data?.data || [];
  const stats: GroupStats = statsData?.data || {
    totalGroups: 0,
    totalDevices: 0,
    unassignedDevices: 0,
    groupsWithDevices: 0,
  };

  // Delete mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => groupsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['groups'] });
      queryClient.invalidateQueries({ queryKey: ['groups-stats'] });
      setSelectedGroup(null);
      setShowDeleteConfirm(false);
    },
  });

  const handleDelete = () => {
    if (selectedGroup) {
      deleteMutation.mutate(selectedGroup.id);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">{t('groups.title')}</h1>
          <p className="text-gray-500 mt-1">{t('groups.subtitle')}</p>
        </div>
        <button
          onClick={() => setShowAddModal(true)}
          className="btn-glow flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          {t('groups.new_group')}
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          icon={<Layers className="h-6 w-6" />}
          label={t('groups.stat_total_groups')}
          value={stats.totalGroups}
          color="cyan"
        />
        <StatCard
          icon={<Car className="h-6 w-6" />}
          label={t('groups.stat_total_vehicles')}
          value={stats.totalDevices}
          color="blue"
        />
        <StatCard
          icon={<FolderOpen className="h-6 w-6" />}
          label={t('groups.stat_active_groups')}
          value={stats.groupsWithDevices}
          color="green"
        />
        <StatCard
          icon={<Users className="h-6 w-6" />}
          label={t('groups.stat_unassigned')}
          value={stats.unassignedDevices}
          color="orange"
        />
      </div>

      {/* Main content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Groups list */}
        <div className="lg:col-span-1 glass-card p-4">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('groups.list_title')}</h2>

          {isLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
            </div>
          ) : groups.length === 0 ? (
            <div className="text-center py-8">
              <FolderOpen className="h-12 w-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">{t('groups.no_group_yet')}</p>
              <button
                onClick={() => setShowAddModal(true)}
                className="mt-4 text-primary-400 hover:text-primary-300 text-sm"
              >
                {t('groups.create_a_group')}
              </button>
            </div>
          ) : (
            <div className="space-y-2">
              {groups.map((group) => (
                <button
                  key={group.id}
                  onClick={() => setSelectedGroup(group)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                    selectedGroup?.id === group.id
                      ? 'bg-primary-500/20 border border-primary-500/30'
                      : 'hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <div
                    className="w-4 h-4 rounded-full flex-shrink-0"
                    style={{ backgroundColor: group.color }}
                  />
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{group.name}</p>
                    <p className="text-xs text-gray-500">
                      {t('groups.vehicles_count', { count: group.deviceCount })}
                      {group.onlineCount > 0 && (
                        <span className="text-emerald-400 ml-2">
                          • {t('groups.online_in_group', { count: group.onlineCount })}
                        </span>
                      )}
                    </p>
                  </div>
                  <ChevronRight className="h-4 w-4 text-gray-400" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Group details */}
        <div className="lg:col-span-2 glass-card p-6">
          {selectedGroup ? (
            <>
              <div className="flex items-start justify-between mb-6">
                <div className="flex items-center gap-3">
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center"
                    style={{ backgroundColor: `${selectedGroup.color}20` }}
                  >
                    <FolderOpen className="h-6 w-6" style={{ color: selectedGroup.color }} />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">{selectedGroup.name}</h2>
                    {selectedGroup.description && (
                      <p className="text-gray-500 text-sm">{selectedGroup.description}</p>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <button
                    onClick={() => setShowAssignModal(true)}
                    className="btn-secondary flex items-center gap-2 text-sm"
                  >
                    <Plus className="h-4 w-4" />
                    {t('groups.add_button')}
                  </button>
                  <button
                    onClick={() => setShowEditModal(true)}
                    className="p-2 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white transition-colors"
                  >
                    <Pencil className="h-5 w-5" />
                  </button>
                  <button
                    onClick={() => setShowDeleteConfirm(true)}
                    className="p-2 rounded-lg hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
                  >
                    <Trash2 className="h-5 w-5" />
                  </button>
                </div>
              </div>

              {/* Devices in group */}
              <div>
                <h3 className="text-sm font-medium text-gray-500 mb-3">
                  {t('groups.vehicles_label', { count: groupDetails?.data?.devices?.length || 0 })}
                </h3>
                {(groupDetails?.data?.devices?.length ?? 0) > 0 ? (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {groupDetails?.data?.devices?.map((device: Device) => (
                      <DeviceCard
                        key={device.id}
                        device={device}
                        groupId={selectedGroup.id}
                        onRemoved={() => {
                          queryClient.invalidateQueries({ queryKey: ['group', selectedGroup.id] });
                          queryClient.invalidateQueries({ queryKey: ['groups'] });
                          queryClient.invalidateQueries({ queryKey: ['groups-stats'] });
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-8 border border-dashed border-gray-300 rounded-xl">
                    <Car className="h-10 w-10 text-gray-400 mx-auto mb-2" />
                    <p className="text-gray-500">{t('groups.no_vehicle_in_group')}</p>
                    <button
                      onClick={() => setShowAssignModal(true)}
                      className="mt-2 text-primary-400 hover:text-primary-300 text-sm"
                    >
                      {t('groups.add_vehicles')}
                    </button>
                  </div>
                )}
              </div>
            </>
          ) : (
            <div className="flex flex-col items-center justify-center h-64 text-center">
              <FolderOpen className="h-16 w-16 text-gray-300 mb-4" />
              <p className="text-gray-500">{t('groups.select_to_view_details')}</p>
            </div>
          )}
        </div>
      </div>

      {/* Add Modal */}
      {showAddModal && (
        <GroupModal
          onClose={() => setShowAddModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
            queryClient.invalidateQueries({ queryKey: ['groups-stats'] });
            setShowAddModal(false);
          }}
        />
      )}

      {/* Edit Modal */}
      {showEditModal && selectedGroup && (
        <GroupModal
          group={selectedGroup}
          onClose={() => setShowEditModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['groups'] });
            setShowEditModal(false);
          }}
        />
      )}

      {/* Assign Modal */}
      {showAssignModal && selectedGroup && (
        <AssignDevicesModal
          groupId={selectedGroup.id}
          onClose={() => setShowAssignModal(false)}
          onSuccess={() => {
            queryClient.invalidateQueries({ queryKey: ['group', selectedGroup.id] });
            queryClient.invalidateQueries({ queryKey: ['groups'] });
            queryClient.invalidateQueries({ queryKey: ['groups-stats'] });
            setShowAssignModal(false);
          }}
        />
      )}

      {/* Delete Confirmation */}
      {showDeleteConfirm && selectedGroup && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content max-w-md" onClick={(e) => e.stopPropagation()}>
            <h3 className="text-lg font-semibold text-gray-900 mb-2">{t('groups.delete_title')}</h3>
            <p className="text-gray-500 mb-6">
              {t('groups.delete_confirm', { name: selectedGroup.name })}
            </p>
            <div className="flex justify-end gap-3">
              <button
                onClick={() => setShowDeleteConfirm(false)}
                className="btn-secondary"
              >
                {t('common.cancel')}
              </button>
              <button
                onClick={handleDelete}
                disabled={deleteMutation.isPending}
                className="btn-danger flex items-center gap-2"
              >
                {deleteMutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                {t('common.delete')}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Stat Card Component
function StatCard({
  icon,
  label,
  value,
  color,
}: {
  icon: React.ReactNode;
  label: string;
  value: number;
  color: 'cyan' | 'blue' | 'green' | 'orange';
}) {
  const colorClasses = {
    cyan: 'text-cyan-400 bg-cyan-500/20',
    blue: 'text-blue-400 bg-blue-500/20',
    green: 'text-emerald-400 bg-emerald-500/20',
    orange: 'text-orange-400 bg-orange-500/20',
  };

  return (
    <div className="glass-card p-4">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${colorClasses[color]}`}>
          {icon}
        </div>
        <div>
          <p className="text-2xl font-bold text-gray-900">{value}</p>
          <p className="text-sm text-gray-500">{label}</p>
        </div>
      </div>
    </div>
  );
}

// Device Card Component
function DeviceCard({
  device,
  groupId,
  onRemoved,
}: {
  device: Device;
  groupId: string;
  onRemoved: () => void;
}) {
  const { t } = useTranslation();
  const removeMutation = useMutation({
    mutationFn: () => groupsApi.removeDevices(groupId, [device.id]),
    onSuccess: onRemoved,
  });

  return (
    <div className="flex items-center justify-between p-3 rounded-xl bg-white/50 border border-white/5">
      <div className="flex items-center gap-3">
        <div className={`w-2 h-2 rounded-full ${
          device.status === 'ONLINE' ? 'bg-emerald-400' : 'bg-gray-400'
        }`} />
        <div>
          <p className="text-sm font-medium text-gray-900">{device.name}</p>
          {device.plate && (
            <p className="text-xs text-gray-500">{device.plate}</p>
          )}
        </div>
      </div>
      <button
        onClick={() => removeMutation.mutate()}
        disabled={removeMutation.isPending}
        className="p-1.5 rounded-lg hover:bg-red-500/20 text-gray-500 hover:text-red-400 transition-colors"
        title={t('groups.remove_from_group')}
      >
        {removeMutation.isPending ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : (
          <X className="h-4 w-4" />
        )}
      </button>
    </div>
  );
}

// Group Modal Component
function GroupModal({
  group,
  onClose,
  onSuccess,
}: {
  group?: Group;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const { selectedTenant } = useTenantContextStore();
  const tenantKey = selectedTenant?.id || 'default';
  const [name, setName] = useState(group?.name || '');
  const [description, setDescription] = useState(group?.description || '');
  const [color, setColor] = useState(group?.color || COLORS[0]);
  const [visibleToUserIds, setVisibleToUserIds] = useState<string[]>(group?.visibleToUserIds || []);
  const [showUsers, setShowUsers] = useState(false);
  const [error, setError] = useState('');

  // Fetch users for visibility selection
  const { data: usersData } = useQuery({
    queryKey: ['users-list', tenantKey],
    queryFn: async () => {
      const r = await usersApi.list({ limit: 500 });
      return r.data;
    },
  });
  const users: Array<{ id: string; firstName: string; lastName: string; email: string; role: string }> = usersData?.data || [];

  const handleUserToggle = (userId: string) => {
    setVisibleToUserIds((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const mutation = useMutation({
    mutationFn: async (data: { name: string; description?: string; color: string; visibleToUserIds: string[] }) => {
      // Two different AxiosResponse<_, D> shapes: unwrap so the union does not leak into the mutation type
      const r = group ? await groupsApi.update(group.id, data) : await groupsApi.create(data);
      return r.data;
    },
    onSuccess,
    onError: (err: any) => {
      setError(err.response?.data?.error || t('groups.generic_error'));
    },
  });

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    mutation.mutate({ name, description: description || undefined, color, visibleToUserIds });
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">
            {group ? t('groups.modal_edit_title') : t('groups.modal_create_title')}
          </h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="p-3 rounded-lg bg-red-500/10 border border-red-500/20 text-red-400 text-sm">
              {error}
            </div>
          )}

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t('groups.field_name')}
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20"
              placeholder={t('groups.field_name_placeholder')}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t('groups.field_description')}
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              className="w-full px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-900 focus:border-primary-500 focus:ring-1 focus:ring-primary-500/20 resize-none"
              rows={3}
              placeholder={t('groups.field_description_placeholder')}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-600 mb-2">
              {t('groups.field_color')}
            </label>
            <div className="flex flex-wrap gap-2">
              {COLORS.map((c) => (
                <button
                  key={c}
                  type="button"
                  onClick={() => setColor(c)}
                  className={`w-8 h-8 rounded-lg transition-all ${
                    color === c ? 'ring-2 ring-white ring-offset-2 ring-offset-white' : ''
                  }`}
                  style={{ backgroundColor: c }}
                />
              ))}
            </div>
          </div>

          {/* Visibility — user selection */}
          <div className="border border-gray-200 rounded-lg">
            <button
              type="button"
              onClick={() => setShowUsers(!showUsers)}
              className="w-full px-4 py-3 flex items-center justify-between hover:bg-gray-50 rounded-lg"
            >
              <div className="flex items-center gap-2">
                <Users className="h-4 w-4 text-gray-500" />
                <span className="font-medium text-gray-900 text-sm">{t('groups.visibility')}</span>
                {visibleToUserIds.length > 0 && (
                  <span className="px-2 py-0.5 text-xs bg-purple-100 text-purple-700 rounded-full">
                    {t('groups.visibility_count', { count: visibleToUserIds.length })}
                  </span>
                )}
              </div>
              {showUsers ? (
                <ChevronUp className="h-4 w-4 text-gray-400" />
              ) : (
                <ChevronDown className="h-4 w-4 text-gray-400" />
              )}
            </button>
            {showUsers && (
              <div className="px-4 pb-4">
                <p className="text-xs text-gray-500 mb-3">
                  {t('groups.visibility_hint')}
                </p>
                <div className="max-h-48 overflow-y-auto space-y-1 border rounded-lg p-2">
                  {users.length === 0 ? (
                    <p className="text-sm text-gray-500 p-2">{t('groups.no_users_available')}</p>
                  ) : (
                    users.map((user) => (
                      <label
                        key={user.id}
                        className="flex items-center gap-3 p-2 rounded-lg cursor-pointer hover:bg-gray-50"
                      >
                        <input
                          type="checkbox"
                          checked={visibleToUserIds.includes(user.id)}
                          onChange={() => handleUserToggle(user.id)}
                          className="rounded border-gray-300 text-green-600 focus:ring-green-500"
                        />
                        <div className="flex-1 min-w-0">
                          <span className="text-sm text-gray-700">
                            {user.firstName} {user.lastName}
                          </span>
                          <span className="text-xs text-gray-400 ml-2">{user.email}</span>
                        </div>
                        <span className="text-xs text-gray-400">{user.role}</span>
                      </label>
                    ))
                  )}
                </div>
              </div>
            )}
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button type="button" onClick={onClose} className="btn-secondary">
              {t('common.cancel')}
            </button>
            <button
              type="submit"
              disabled={mutation.isPending || !name}
              className="btn-glow flex items-center gap-2"
            >
              {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
              {group ? t('common.save') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Assign Devices Modal Component
function AssignDevicesModal({
  groupId,
  onClose,
  onSuccess,
}: {
  groupId: string;
  onClose: () => void;
  onSuccess: () => void;
}) {
  const { t } = useTranslation();
  const [selectedDevices, setSelectedDevices] = useState<string[]>([]);

  // Fetch all devices
  const { data: devicesData, isLoading } = useQuery({
    queryKey: ['devices-for-assign'],
    queryFn: () => devicesApi.list({ limit: 100 }),
  });

  // Fetch unassigned devices
  const { data: unassignedData } = useQuery({
    queryKey: ['unassigned-devices'],
    queryFn: () => groupsApi.getUnassigned(),
  });

  const devices = devicesData?.data?.data || [];
  const unassignedIds = new Set((unassignedData?.data?.data || []).map((d: Device) => d.id));

  // Filter to show only unassigned devices
  const availableDevices = devices.filter((d: Device) => unassignedIds.has(d.id));

  const mutation = useMutation({
    mutationFn: (deviceIds: string[]) => groupsApi.addDevices(groupId, deviceIds),
    onSuccess,
  });

  const toggleDevice = (deviceId: string) => {
    setSelectedDevices((prev) =>
      prev.includes(deviceId)
        ? prev.filter((id) => id !== deviceId)
        : [...prev, deviceId]
    );
  };

  const handleSubmit = () => {
    if (selectedDevices.length > 0) {
      mutation.mutate(selectedDevices);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-semibold text-gray-900">{t('groups.add_vehicles')}</h2>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-white/10 text-gray-500 hover:text-white"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-primary-400" />
          </div>
        ) : availableDevices.length === 0 ? (
          <div className="text-center py-8">
            <Car className="h-12 w-12 text-gray-400 mx-auto mb-3" />
            <p className="text-gray-500">{t('groups.all_assigned')}</p>
          </div>
        ) : (
          <>
            <div className="space-y-2 max-h-80 overflow-y-auto">
              {availableDevices.map((device: Device) => (
                <button
                  key={device.id}
                  onClick={() => toggleDevice(device.id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl transition-all ${
                    selectedDevices.includes(device.id)
                      ? 'bg-primary-500/20 border border-primary-500/30'
                      : 'hover:bg-white/5 border border-transparent'
                  }`}
                >
                  <div className={`w-5 h-5 rounded border flex items-center justify-center ${
                    selectedDevices.includes(device.id)
                      ? 'bg-primary-500 border-primary-500'
                      : 'border-gray-400'
                  }`}>
                    {selectedDevices.includes(device.id) && (
                      <Check className="h-3 w-3 text-white" />
                    )}
                  </div>
                  <div className="flex-1 text-left">
                    <p className="font-medium text-gray-900">{device.name}</p>
                    {device.plate && (
                      <p className="text-xs text-gray-500">{device.plate}</p>
                    )}
                  </div>
                  <div className={`w-2 h-2 rounded-full ${
                    device.status === 'ONLINE' ? 'bg-emerald-400' : 'bg-gray-400'
                  }`} />
                </button>
              ))}
            </div>

            <div className="flex justify-between items-center pt-4 mt-4 border-t border-white/10">
              <p className="text-sm text-gray-500">
                {t('groups.selected_count', { count: selectedDevices.length })}
              </p>
              <div className="flex gap-3">
                <button onClick={onClose} className="btn-secondary">
                  {t('common.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={mutation.isPending || selectedDevices.length === 0}
                  className="btn-glow flex items-center gap-2"
                >
                  {mutation.isPending && <Loader2 className="h-4 w-4 animate-spin" />}
                  {t('groups.add_button')}
                </button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
